import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  FileText, 
  AlertCircle, 
  CheckCircle,
  Info,
  Edit3,
  Tag,
  CalendarDays,
  ChevronDown,
  Copy,
  UserCheck,
  Image as ImageIcon,
  Upload,
  X,
  Square,
  Circle,
  Heart,
  Star,
  Link,
  Globe,
  Shirt
} from 'lucide-react';
import { Event, EventFormData, EventCategory, getEffectiveEventStatus } from '../../types/event';
import Input from '../UI/Input';
import Button from '../UI/Button';
import { useAppSelector } from '../../store/hooks';
import { logError } from '../../utils/errorLogger';
import { getImageShapeStyles } from '../../utils/imageShapeStyles';

interface EnhancedEventDetailsFormProps {
  event: Event;
  isEditing: boolean;
  editData: Partial<EventFormData>;
  onFieldChange: (field: keyof EventFormData, value: any) => void;
  errors?: Record<string, string>;
  categories?: EventCategory[];
}

interface FormSectionProps {
  title: string;
  description?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  variant?: 'default' | 'highlighted' | 'warning';
  forceExpanded?: boolean;
  expandTrigger?: number;
}

const FormSection: React.FC<FormSectionProps> = ({ 
  title, 
  description,
  icon, 
  children, 
  collapsible = false, 
  defaultExpanded = true,
  variant = 'default',
  forceExpanded,
  expandTrigger
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Update local state when expand/collapse all is triggered
  useEffect(() => {
    if (expandTrigger && expandTrigger > 0) {
      setIsExpanded(forceExpanded === true);
    }
  }, [expandTrigger, forceExpanded]);
  
  // Use local state for expansion, but allow forceExpanded to override temporarily
  const actuallyExpanded = isExpanded;

  const variantClasses = {
    default: {
      container: theme === 'dark' ? 'bg-gray-900/80 border-gray-700/50' : 'bg-white border-gray-200',
      header: theme === 'dark' ? 'bg-gray-800/50 border-gray-700/50' : 'bg-gray-50 border-gray-200',
      icon: theme === 'dark' ? 'text-mirsal-green bg-green-900/30' : 'text-mirsal-green bg-green-100'
    },
    highlighted: {
      container: theme === 'dark' ? 'bg-green-900/20 border-green-700/50' : 'bg-green-50 border-green-200',
      header: theme === 'dark' ? 'bg-green-900/30 border-green-700/50' : 'bg-green-100 border-green-200',
      icon: theme === 'dark' ? 'text-mirsal-green bg-green-800/30' : 'text-mirsal-green bg-green-200'
    },
    warning: {
      container: theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700/50' : 'bg-yellow-50 border-yellow-200',
      header: theme === 'dark' ? 'bg-yellow-900/30 border-yellow-700/50' : 'bg-yellow-100 border-yellow-200',
      icon: theme === 'dark' ? 'text-yellow-300 bg-yellow-800/30' : 'text-yellow-700 bg-yellow-200'
    }
  };

  const classes = variantClasses[variant];

  return (
    <div className={`${classes.container} border rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg`}>
      <div 
        className={`flex items-center justify-between p-5 ${classes.header} border-b transition-colors duration-200 ${
          collapsible ? 'cursor-pointer hover:bg-opacity-80 hover:shadow-sm' : ''
        }`}
        onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center">
          <div className={`w-10 h-10 ${classes.icon} rounded-lg flex items-center justify-center mr-4`}>
            {React.cloneElement(icon as React.ReactElement<any>, { className: 'h-5 w-5' })}
          </div>
          <div>
            <h3 className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>{title}</h3>
            {description && (
              <p className={`text-sm mt-1 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>{description}</p>
            )}
          </div>
        </div>
        {collapsible && (
          <div className={`transform transition-transform duration-200 ${actuallyExpanded ? 'rotate-180' : ''}`}>
            <ChevronDown className={`h-5 w-5 hover:text-mirsal-green ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
        )}
      </div>
      
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        (!collapsible || actuallyExpanded) ? 'opacity-100 max-h-screen' : 'opacity-0 max-h-0'
      }`}>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const FieldGroup: React.FC<{ 
  label: string; 
  children: React.ReactNode; 
  error?: string;
  required?: boolean;
  help?: string;
  icon?: React.ReactNode;
}> = ({ label, children, error, required, help, icon }) => {
  const { theme } = useAppSelector((state) => state.ui);
  
  return (
    <div className="space-y-2">
      <label className={`flex items-center text-sm font-medium ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
      }`}>
        {icon && (
          <span className={`mr-2 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
          }`}>
            {React.cloneElement(icon as React.ReactElement<any>, { className: 'h-4 w-4' })}
          </span>
        )}
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {help && (
        <p className={`text-xs flex items-center ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <Info className="h-3 w-3 mr-1" />
          {help}
        </p>
      )}
      {children}
      {error && (
        <div className={`flex items-center text-sm p-2 rounded-lg ${
          theme === 'dark' 
            ? 'text-red-400 bg-red-900/30 border border-red-700/50' 
            : 'text-red-600 bg-red-50'
        }`}>
          <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

const ReadOnlyField: React.FC<{
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  copyable?: boolean;
}> = ({ label, value, icon, copyable }) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <label className={`flex items-center text-sm font-medium ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
      }`}>
        {icon && (
          <span className={`mr-2 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
          }`}>
            {React.cloneElement(icon as React.ReactElement<any>, { className: 'h-4 w-4' })}
          </span>
        )}
        {label}
      </label>
      <div className="flex items-center">
        <div className={`flex-1 p-3 border rounded-lg text-sm ${
          theme === 'dark' 
            ? 'bg-gray-800/50 border-gray-700/50 text-gray-300' 
            : 'bg-gray-50 border-gray-200 text-gray-900'
        }`}>
          {value}
        </div>
        {copyable && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCopy}
            className="ml-2"
          >
            {copied ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export const EnhancedEventDetailsForm: React.FC<EnhancedEventDetailsFormProps> = ({
  event,
  isEditing,
  editData,
  onFieldChange,
  errors = {},
  categories = []
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const effectiveStatus = getEffectiveEventStatus(event);
  const [expandAll, setExpandAll] = useState(false);
  const [expandTrigger, setExpandTrigger] = useState(0);

  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Get current image values (from editData if editing, otherwise from event)
  const currentImageUrl = isEditing && editData.image_url ? editData.image_url : event.image_url;
  const currentImageShape = isEditing && editData.image_shape ? editData.image_shape : (event.image_shape || 'square');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Image validation functions
  const validateImageFile = (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB
    const minSize = 1024; // 1KB

    if (!allowedTypes.includes(file.type.toLowerCase())) {
      return {
        isValid: false,
        error: `Invalid file type. Only JPG, JPEG, PNG, GIF, or WebP images are allowed.`
      };
    }

    if (!file.name || file.name.trim().length === 0) {
      return {
        isValid: false,
        error: 'Invalid filename. Please select a file with a valid name.'
      };
    }

    const lastDotIndex = file.name.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === file.name.length - 1) {
      return {
        isValid: false,
        error: 'Invalid file extension. Please select a valid image file.'
      };
    }

    const extension = file.name.substring(lastDotIndex).toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return {
        isValid: false,
        error: `Invalid file extension (${extension}). Only ${allowedExtensions.join(', ')} are allowed.`
      };
    }

    if (file.size < minSize) {
      return {
        isValid: false,
        error: 'File is too small. Minimum size is 1KB.'
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File is too large. Maximum size is 2MB.'
      };
    }

    return { isValid: true, error: null };
  };

  const validateImageContent = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = e.target?.result as string;
      };
      reader.onerror = () => resolve(false);
      reader.readAsDataURL(file);
    });
  };

  // Handle image upload
  const handleImageUpload = async (file: File) => {
    if (!file) return;

    const validationResult = validateImageFile(file);
    if (!validationResult.isValid) {
      setImageError(validationResult.error || 'Invalid image file');
      return;
    }

    try {
      setImageUploading(true);
      setImageError(null);

      const isImageValid = await validateImageContent(file);
      if (!isImageValid) {
        setImageError('Invalid image file. Please select a valid image.');
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const response = await fetch('/api/upload/image/', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `Upload failed with status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData?.error) {
            errorMessage = errorData.error;
          } else if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          logError('Failed to parse error response', parseError);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (!result.image_url || typeof result.image_url !== 'string') {
        throw new Error('Invalid response from server');
      }

      if (!result.image_id || typeof result.image_id !== 'string') {
        throw new Error('Invalid image ID received from server');
      }

      // Update form data with new image
      onFieldChange('image_url', result.image_url);
      onFieldChange('image_id', result.image_id);
      
    } catch (err: any) {
      logError('Failed to upload image', err);
      setImageError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle remove image
  const handleRemoveImage = () => {
    onFieldChange('image_url', null);
    onFieldChange('image_id', null);
    setImageError(null);
  };

  // Handle image shape change
  const handleImageShapeChange = (shape: 'circle' | 'square' | 'heart' | 'star') => {
    onFieldChange('image_shape', shape);
  };


  return (
    <div className="space-y-6">
      {/* SVG Definition for Heart Clip Path */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <clipPath id="heartClipPath" clipPathUnits="objectBoundingBox">
            <path d="M0.5,1
                     C0.5,1 0,0.65 0,0.35
                     A0.25,0.25 0 0,1 0.5,0.35
                     A0.25,0.25 0 0,1 1,0.35
                     C1,0.65 0.5,1 0.5,1 Z" />
          </clipPath>
        </defs>
      </svg>

      {/* Event Status Banner - Rejection reason display removed as requested */}

      {effectiveStatus === 'pending' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start">
            <Clock className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-yellow-900 mb-1">Pending Approval</h4>
              <p className="text-sm text-yellow-700">This event is waiting for admin approval before it becomes active.</p>
            </div>
          </div>
        </div>
      )}

      {/* Accordion Controls */}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setExpandAll(!expandAll);
            setExpandTrigger(prev => prev + 1);
          }}
          className="text-mirsal-green hover:text-mirsal-black hover:bg-green-50"
        >
          {expandAll ? (
            <>
              <ChevronDown className="h-4 w-4 mr-2 rotate-180" />
              Collapse All
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-2" />
              Expand All
            </>
          )}
        </Button>
      </div>

      {/* Basic Information */}
      <FormSection
        title="Basic Information"
        description="Core event details and timing"
        icon={<FileText />}
        variant="highlighted"
        collapsible
        defaultExpanded={false}
        forceExpanded={expandAll}
        expandTrigger={expandTrigger}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-2">
            <FieldGroup 
              label="Event Title" 
              required 
              error={errors.title}
              icon={<Edit3 />}
              help="Event title cannot be changed after creation"
            >
              <ReadOnlyField label="" value={event.title} />
            </FieldGroup>
          </div>

          <FieldGroup 
            label="Date" 
            required 
            error={errors.event_date}
            icon={<Calendar />}
          >
            {isEditing ? (
              <Input
                type="date"
                value={editData.event_date || event.event_date}
                onChange={(e) => onFieldChange('event_date', e.target.value)}
              />
            ) : (
              <ReadOnlyField label="" value={formatDate(event.event_date)} />
            )}
          </FieldGroup>

          <FieldGroup 
            label="Time" 
            required 
            error={errors.event_time}
            icon={<Clock />}
          >
            {isEditing ? (
              <Input
                type="time"
                value={editData.event_time || event.event_time}
                onChange={(e) => onFieldChange('event_time', e.target.value)}
              />
            ) : (
              <ReadOnlyField label="" value={formatTime(event.event_time)} />
            )}
          </FieldGroup>

          <div className="lg:col-span-2">
            <FieldGroup 
              label="Location" 
              required 
              error={errors.location}
              icon={<MapPin />}
              help="Full address or venue name"
            >
              {isEditing ? (
                <Input
                  value={editData.location || event.location}
                  onChange={(e) => onFieldChange('location', e.target.value)}
                  placeholder="Enter event location"
                />
              ) : (
                <ReadOnlyField label="" value={event.location} copyable />
              )}
            </FieldGroup>
          </div>

          <FieldGroup
            label="Location Link"
            required
            error={errors.location_link}
            icon={<Link />}
            help="Google Maps (or similar) link for this venue"
          >
            {isEditing ? (
              <Input
                type="url"
                value={editData.location_link ?? event.location_link ?? ''}
                onChange={(e) => onFieldChange('location_link', e.target.value)}
                placeholder="e.g., https://maps.google.com/..."
              />
            ) : (
              <ReadOnlyField
                label=""
                value={event.location_link || 'Not set'}
                copyable={Boolean(event.location_link)}
              />
            )}
          </FieldGroup>

          <FieldGroup
            label="Website Link"
            error={errors.website_link}
            icon={<Globe />}
            help="Link to event website"
          >
            {isEditing ? (
              <Input
                type="url"
                value={editData.website_link ?? event.website_link ?? ''}
                onChange={(e) => onFieldChange('website_link', e.target.value)}
                placeholder="e.g., https://event-website.com"
              />
            ) : (
              <ReadOnlyField
                label=""
                value={event.website_link || 'Not set'}
                copyable={Boolean(event.website_link)}
              />
            )}
          </FieldGroup>

          <div className="lg:col-span-2">
            <FieldGroup
              label="Dress Code"
              error={errors.dress_code}
              icon={<Shirt />}
              help="Dress code information for the event"
            >
              {isEditing ? (
                <Input
                  value={editData.dress_code ?? event.dress_code ?? ''}
                  onChange={(e) => onFieldChange('dress_code', e.target.value)}
                  placeholder="e.g., Formal, Casual, Black Tie"
                />
              ) : (
                <ReadOnlyField label="" value={event.dress_code || 'Not set'} />
              )}
            </FieldGroup>
          </div>

          {event.description && (
            <div className="lg:col-span-2">
              <FieldGroup 
                label="Description" 
                error={errors.description}
                icon={<FileText />}
              >
                {isEditing ? (
                  <textarea
                    value={editData.description || event.description || ''}
                    onChange={(e) => onFieldChange('description', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Optional event description"
                  />
                ) : (
                  <ReadOnlyField label="" value={event.description || 'No description provided'} />
                )}
              </FieldGroup>
            </div>
          )}
        </div>
      </FormSection>

      {/* Event Image */}
      <FormSection
        title="Event Image"
        description="Upload and customize the event image"
        icon={<ImageIcon />}
        collapsible
        defaultExpanded={false}
        forceExpanded={expandAll}
        expandTrigger={expandTrigger}
      >
        <div className="space-y-6">
          {/* Hidden file input - Always render when editing */}
          {isEditing && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileInputChange}
              className="hidden"
            />
          )}

          {/* Image Preview and Controls */}
          <div className="flex flex-col md:flex-row gap-6 items-center justify-center min-h-[280px]">
            {/* Action Buttons - Left side when editing */}
            {isEditing && (
              <div className="flex flex-col gap-3 w-full md:w-auto items-center md:items-start">
                {/* Change Image Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                  className="w-full md:w-auto"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Change Image
                </Button>

                {/* Image Shape Selector */}
                <div className="flex gap-2 justify-center md:justify-start">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleImageShapeChange('square');
                    }}
                    className={`p-2 rounded-md transition-colors shadow-md ${
                      currentImageShape === 'square'
                        ? 'bg-mirsal-green text-white'
                        : theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                    title="Square shape"
                  >
                    <Square className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleImageShapeChange('circle');
                    }}
                    className={`p-2 rounded-md transition-colors shadow-md ${
                      currentImageShape === 'circle'
                        ? 'bg-mirsal-green text-white'
                        : theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                    title="Circle shape"
                  >
                    <Circle className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleImageShapeChange('heart');
                    }}
                    className={`p-2 rounded-md transition-colors shadow-md ${
                      currentImageShape === 'heart'
                        ? 'bg-mirsal-green text-white'
                        : theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                    title="Heart shape"
                  >
                    <Heart className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleImageShapeChange('star');
                    }}
                    className={`p-2 rounded-md transition-colors shadow-md ${
                      currentImageShape === 'star'
                        ? 'bg-mirsal-green text-white'
                        : theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                    title="Star shape"
                  >
                    <Star className="h-4 w-4" />
                  </button>
                </div>

                {/* Remove Image Button */}
                {currentImageUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50 w-full md:w-auto"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove Image
                  </Button>
                )}
              </div>
            )}

            {/* Image Preview - Center */}
            {currentImageUrl ? (
              <div 
                className={`relative w-64 h-64 overflow-hidden border-2 flex-shrink-0 ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-300'
                } ${getImageShapeStyles(currentImageShape).className || 'rounded-lg'}`}
                style={getImageShapeStyles(currentImageShape).style}
              >
                <img
                  src={currentImageUrl}
                  alt="Event"
                  className="w-full h-full object-cover"
                  style={getImageShapeStyles(currentImageShape).style}
                />
              </div>
            ) : (
              <div className="flex-shrink-0">
                {isEditing ? (
                  <div 
                    className={`w-64 h-64 border-2 border-dashed rounded-lg flex flex-col items-center justify-center ${
                      theme === 'dark' 
                        ? 'border-gray-600 bg-gray-800/50' 
                        : 'border-gray-300 bg-gray-50'
                    } ${imageUploading ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:border-mirsal-green'} transition-colors`}
                    onClick={() => !imageUploading && fileInputRef.current?.click()}
                  >
                    {imageUploading ? (
                      <div className="flex flex-col items-center">
                        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
                          theme === 'dark' ? 'border-[#33FF00]' : 'border-mirsal-green'
                        }`}></div>
                        <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Uploading...
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload className={`h-12 w-12 mb-2 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                        <p className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Click to upload image
                        </p>
                        <p className={`text-xs mt-1 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          JPG, PNG, GIF, or WebP (max 2MB)
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={`w-64 h-64 border-2 border-dashed rounded-lg flex flex-col items-center justify-center ${
                    theme === 'dark' 
                      ? 'border-gray-600 bg-gray-800/50' 
                      : 'border-gray-300 bg-gray-50'
                  }`}>
                    <ImageIcon className={`h-12 w-12 mb-2 ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      No image uploaded
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {imageError && (
            <div className={`flex items-center text-sm p-3 rounded-lg ${
              theme === 'dark' 
                ? 'text-red-400 bg-red-900/30 border border-red-700/50' 
                : 'text-red-600 bg-red-50 border border-red-200'
            }`}>
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              {imageError}
            </div>
          )}

          {/* Help Text */}
          {isEditing && (
            <div className={`text-xs ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <Info className="h-3 w-3 inline mr-1" />
              The event image will be displayed on invitation pages. Supported formats: JPG, PNG, GIF, WebP. Maximum size: 2MB.
            </div>
          )}
        </div>
      </FormSection>

      {/* Organization */}
      <FormSection
        title="Organization"
        description="Category and series information"
        icon={<Tag />}
        collapsible
        defaultExpanded={false}
        forceExpanded={expandAll}
        expandTrigger={expandTrigger}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FieldGroup 
            label="Category" 
            error={errors.category_id}
            icon={<Tag />}
            help="Select the event category"
          >
            {isEditing ? (
              <select
                value={editData.category_id || event.category?.id || ''}
                onChange={(e) => onFieldChange('category_id', e.target.value ? parseInt(e.target.value) : null)}
                className={`block w-full rounded-md shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm ${
                  errors.category_id 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : theme === 'dark'
                      ? 'border-gray-600 bg-gray-800 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                <option value="">Select a category (optional)</option>
                {Array.isArray(categories) && categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            ) : (
              <ReadOnlyField 
                label="" 
                value={event.category?.name || 'No category assigned'}
                icon={<Tag />}
              />
            )}
          </FieldGroup>

          {event.series_name && (
            <FieldGroup 
              label="Event Series" 
              icon={<CalendarDays />}
              help="Series information (read-only)"
            >
              <ReadOnlyField 
                label="" 
                value={`${event.series_name}${event.series_position ? ` (Event #${event.series_position})` : ''}`}
                // Remove icon prop - FieldGroup already displays it
              />
            </FieldGroup>
          )}
        </div>
      </FormSection>

      {/* Guest Settings */}
      <FormSection
        title="Guest Settings"
        description="Capacity and guest management options"
        icon={<Users />}
        collapsible
        defaultExpanded={false}
        forceExpanded={expandAll}
        expandTrigger={expandTrigger}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FieldGroup 
            label="Guest Limit" 
            error={errors.guest_limit}
            icon={<Users />}
            help="Leave empty for unlimited capacity"
          >
            {isEditing ? (
              <Input
                type="number"
                value={editData.guest_limit?.toString() || event.guest_limit?.toString() || ''}
                onChange={(e) => onFieldChange('guest_limit', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="No limit"
                min="1"
              />
            ) : (
              <ReadOnlyField label="" value={event.guest_limit || 'Unlimited'} />
            )}
          </FieldGroup>

        </div>

        {/* Current Capacity Display */}
        {!isEditing && (
              <div className={`mt-6 p-4 border rounded-lg ${
                theme === 'dark' 
                  ? 'bg-green-900/20 border-green-700/50' 
                  : 'bg-green-50 border-green-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-mirsal-black'
                  }`}>Current Attendance</span>
                  <span className={`text-sm ${
                    theme === 'dark' ? 'text-green-300' : 'text-mirsal-green'
                  }`}>
                    {event.accepted_count || 0}{event.guest_limit ? ` / ${event.guest_limit}` : ''} guests
                  </span>
                </div>
                {event.guest_limit && (
                  <div className={`w-full rounded-full h-2 ${
                    theme === 'dark' ? 'bg-green-800/50' : 'bg-green-200'
                  }`}>
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        event.is_full ? 'bg-red-500' : 'bg-mirsal-green'
                      }`}
                  style={{
                    width: `${Math.min(((event.accepted_count || 0) / event.guest_limit) * 100, 100)}%`
                  }}
                />
              </div>
            )}
                {event.pending_count && event.pending_count > 0 && (
                  <p className={`text-xs mt-2 ${
                    theme === 'dark' ? 'text-green-400' : 'text-green-600'
                  }`}>
                    {event.pending_count} guests have pending responses
                  </p>
                )}
          </div>
        )}
      </FormSection>
    </div>
  );
};

export default EnhancedEventDetailsForm;
